import React, { useState, useEffect, useMemo, useRef } from "react";
import { Helmet } from "react-helmet-async";
import Sidenav from "../components/Sidenav";
import CreateProduct from "../components/CreateProduct";
import EditProduct from "../components/EditProduct";
import DeleteProductSKUModal from "../components/DeleteProductSKUModal";
import "../Console.css";

const API_BASE = "https://api.iriscrypt.com";
const API_KEY = "f87f030d-eaf4-4a39-9d5d-c9ffdb90ab43";

/* ------------------ Small helpers ------------------ */
const styles = (
  <style>{`
    .card-grid { display:grid; gap:14px; grid-template-columns:repeat(3,minmax(0,1fr)); }
    @media (max-width:1200px) { .card-grid { grid-template-columns:repeat(2,minmax(0,1fr)); } }
    @media (max-width:760px)  { .card-grid { grid-template-columns:1fr; } }

    .sku-toolbar {
      display:flex; align-items:center; justify-content:space-between; margin:0 0 14px 0;
    }
    .count-badge {
      display:inline-flex; align-items:center; gap:7px;
      background:#fff; border:1px solid #e5e7eb; color:#111827;
      font-size:12px; font-weight:800; padding:7px 13px; border-radius:9999px;
      box-shadow:0 1px 3px rgba(0,0,0,0.05);
    }

    .sku-card {
      background:#fff; border:1px solid #e5e7eb; border-radius:16px;
      padding:16px; box-shadow:0 1px 3px rgba(0,0,0,0.04),0 8px 24px rgba(0,0,0,0.05);
      color:#111827; font-size:13px; line-height:1.4; position:relative;
      transition:box-shadow 0.15s, transform 0.15s;
    }
    .sku-card:hover {
      box-shadow:0 4px 16px rgba(0,0,0,0.08),0 12px 32px rgba(0,0,0,0.07);
      transform:translateY(-1px);
    }

    .sku-header { display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:12px; }
    .sku-left   { display:flex; align-items:center; gap:10px; min-width:0; }

    .sku-avatar {
      width:44px; height:44px; border-radius:12px; flex-shrink:0;
      display:flex; align-items:center; justify-content:center;
      font-weight:800; font-size:14px; color:#fff; text-transform:uppercase;
      border:1px solid rgba(0,0,0,0.08);
    }
    .sku-name {
      font-weight:800; font-size:15px; line-height:1.2;
      white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
    }
    .sku-code {
      display:inline-flex; align-items:center; gap:4px; padding:2px 7px; margin-top:3px;
      border-radius:9999px; background:#f3f4f6; color:#6b7280;
      font-size:10px; font-weight:700; white-space:nowrap;
      font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
    }

    .divider { height:1px; background:#f3f4f6; margin:12px 0; }

    .meta-stack { display:flex; flex-direction:column; gap:7px; }
    .meta-item  { display:grid; grid-template-columns:130px 1fr; column-gap:8px; font-size:12.5px; align-items:start; }
    .meta-icon-label {
      display:inline-flex; align-items:flex-start; gap:5px;
      font-weight:700; color:#9ca3af; font-size:11px;
      word-break:break-word; overflow-wrap:anywhere;
    }
    .meta-icon-label i { font-size:11px; width:12px; text-align:center; margin-top:1px; flex-shrink:0; }
    .meta-content { color:#111827; overflow-wrap:anywhere; word-break:break-word; min-width:0; }

    .empty-state {
      background:#fff; border:1px dashed #e5e7eb; border-radius:12px; padding:32px;
      text-align:center; color:#6b7280; font-size:13px;
    }

    .card-actions { position:relative; }
    .icon-ellipsis {
      display:inline-flex; align-items:center; justify-content:center;
      width:34px; height:34px; border-radius:9px;
      background:#f9fafb; color:#111827; border:1px solid #e5e7eb; font-size:13px;
    }
    .icon-ellipsis:hover { background:#f3f4f6; }
    .icon-ellipsis:focus { outline:2px solid #d1d5db; }

    .menu-pop {
      position:absolute; top:38px; right:0;
      background:#fff; border:1px solid #e5e7eb; border-radius:12px;
      box-shadow:0 10px 30px rgba(0,0,0,0.08);
      min-width:180px; padding:6px; z-index:50;
    }
    .menu-item {
      width:100%; display:flex; align-items:center; gap:10px;
      border:none; background:transparent; text-align:left;
      padding:8px 10px; border-radius:8px; font-size:13px; color:#111827; font-weight:700;
    }
    .menu-item i { font-size:14px; }
    .menu-item:hover { background:#f3f4f6; }
    .menu-item.danger { color:#b91c1c; }
    .menu-item.danger:hover { background:#fff1f2; }
    .menu-item:disabled { opacity:.6; cursor:not-allowed; }

    .search-wrap {
      position:sticky; top:0; z-index:10;
      background:#ebebeb; padding:12px 20px 10px 20px;
    }
    .search-bar {
      display:flex; align-items:center; gap:10px;
      background:#fff; border:1.5px solid #e5e7eb; border-radius:9999px;
      padding:10px 16px; box-shadow:0 1px 3px rgba(0,0,0,0.05);
      transition:border-color 0.15s, box-shadow 0.15s;
    }
    .search-bar.focused { border-color:#ff5100; box-shadow:0 0 0 3px rgba(255,81,0,0.1); }
    .search-icon { color:#9ca3af; font-size:14px; flex-shrink:0; transition:color 0.15s; }
    .search-bar.focused .search-icon { color:#ff5100; }
    .search-input { flex:1; border:none; outline:none; background:transparent; font-size:14px; color:#111827; }
    .search-input::placeholder { color:#9ca3af; }
    .search-shortcut {
      font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
      font-size:11px; font-weight:700;
      background:#f3f4f6; border:1px solid #e5e7eb; border-bottom-width:2px;
      border-radius:5px; padding:1px 6px; color:#6b7280;
      user-select:none; flex-shrink:0;
    }
    .search-clear {
      border:1px solid #e5e7eb; background:#f3f4f6; color:#374151;
      padding:3px 10px; border-radius:9999px; font-size:12px; font-weight:700;
      flex-shrink:0; cursor:pointer;
    }
    .search-clear:hover { background:#e5e7eb; }
  `}</style>
);

function getSkuInitials(name) {
  if (!name) return "SK";
  const parts = name.trim().split(/\s+/);
  return parts.slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}

function getSkuColor(str) {
  const palette = ["#4f46e5","#0ea5e9","#22c55e","#f59e0b","#ef4444","#a855f7","#14b8a6","#f97316"];
  let hash = 0;
  for (let i = 0; i < (str || "").length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return palette[Math.abs(hash) % palette.length];
}

function metaIcon(key) {
  const k = (key || "").toLowerCase();
  // Logistics & shipping
  if (k.includes("truck"))                                               return "fa-truck";
  if (k.includes("delivery"))                                            return "fa-truck-fast";
  if (k.startsWith("import"))                                            return "fa-ship";
  // Compliance & legal
  if (k.includes("certif"))                                              return "fa-certificate";
  if (k.includes("licens"))                                              return "fa-id-card";
  // Identifiers
  if (k.includes("serial"))                                              return "fa-barcode";
  if (k.includes("barcode") || k.includes("ean") || k.includes("upc"))  return "fa-barcode";
  if (k.includes("number") || k === "no" || k === "num")                return "fa-hashtag";
  // Contact & people
  if (k.includes("contact") || k.includes("phone"))                     return "fa-phone";
  if (k.includes("manufacturer") || k === "mfg" || k.startsWith("mfg ") || k.startsWith("made")) return "fa-industry";
  // Commerce
  if (k.startsWith("sold"))                                              return "fa-cart-shopping";
  if (k.includes("price") || k.includes("cost"))                        return "fa-tag";
  if (k.startsWith("count") || k.includes("quantity"))                  return "fa-list-ol";
  if (k.includes("capacity"))                                            return "fa-gauge";
  // Dates & time — most specific first
  if (k.includes("expir"))                                               return "fa-calendar";
  if (k.startsWith("date") || k === "day" || k === "month" || k === "year") return "fa-calendar";
  // Dimensions — order matters (length before weight, width before height)
  if (k.includes("length"))                                              return "fa-ruler-horizontal";
  if (k.includes("width"))                                               return "fa-arrows-left-right";
  if (k.includes("height"))                                              return "fa-arrows-up-down";
  if (k.includes("weight"))                                              return "fa-weight-hanging";
  if (k.includes("volume"))                                              return "fa-cube";
  if (k.includes("size"))                                                return "fa-ruler";
  if (k === "age" || k.startsWith("age "))                              return "fa-hourglass";
  // Location & web
  if (k.includes("venue") || k.includes("location"))                    return "fa-location-dot";
  if (k.includes("origin") || k.includes("country") || k.includes("product of")) return "fa-globe";
  if (k.includes("website") || k.includes("url") || k === "link")       return "fa-globe";
  // Content & notes
  if (k.includes("ingredient"))                                          return "fa-flask";
  if (k.includes("desc"))                                                return "fa-align-left";
  if (k.startsWith("note") || k.includes("remark"))                     return "fa-note-sticky";
  if (k.includes("page"))                                                return "fa-file-lines";
  // Product attributes
  if (k.includes("ideal") || k.includes("suitable"))                    return "fa-heart";
  if (k.includes("color") || k.includes("colour"))                      return "fa-palette";
  if (k.includes("category") || k.includes("type"))                     return "fa-th-large";
  if (k.includes("brand"))                                               return "fa-copyright";
  return "fa-circle-dot";
}

const ProductSKUs = ({ domain, userData }) => {
  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [editProductId, setEditProductId] = useState("");
  const [items, setItems] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showDeleteSKUModal, setShowDeleteSKUModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchFocused, setSearchFocused] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "/") return;
      const tag = document.activeElement?.tagName?.toUpperCase();
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (showCreateProductModal || showEditProductModal || showDeleteSKUModal) return;
      e.preventDefault();
      searchInputRef.current?.focus();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [showCreateProductModal, showEditProductModal, showDeleteSKUModal]);
  const [userDataState, setUserDataState] = useState(userData);
  const [search, setSearch] = useState("");
  const [
    userId,
    userType,
    userName,
    userEmail,
    userPassword,
    userOrg,
    userPermissions,
    userAnonymous,
  ] = userDataState || [];

  useEffect(() => {
    setUserDataState(userData);
  }, [userData]);

  // close menus on any outer click
  useEffect(() => {
    const onDocClick = () => setOpenMenuId(null);
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const handleCloseCreateProductModal = () => setShowCreateProductModal(false);
  const handleShowCreateProductModal = () => setShowCreateProductModal(true);

  const handleEditProduct = (item) => {
    setOpenMenuId(null);
    setEditProductId(item._id);
    setShowEditProductModal(true);
  };
  const handleCloseEditProductModal = () => {
    setShowEditProductModal(false);
    setEditProductId("");
  };

  // Ready when we have the info needed to choose the endpoint
  const ready = useMemo(() => {
    if (!userType) return false;
    if (userType === "brandadmin" && !userOrg) return false;
    return true;
  }, [userType, userOrg]);

  const fetchItems = async () => {
    try {
      setIsLoading(true);

      const endpoint =
        userType === "brandadmin"
          ? `${API_BASE}/item/by-client/${encodeURIComponent(
              String(userOrg || "").trim()
            )}`
          : `${API_BASE}/item`;

      const response = await fetch(endpoint, {
        method: "GET",
        headers: { "x-api-key": API_KEY },
      });
      const data = await response.json();

      const formatted = (data || []).map((item) => ({
        _id: item._id,
        code: item.code,
        name: item.metadata?.name || "",
        metadata: Object.fromEntries(
          Object.entries(item.metadata || {}).filter(([key]) => key !== "name")
        ),
      }));

      setItems(formatted);
    } catch (error) {
      console.error("Error fetching items:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch when user data becomes ready or when relevant parts change
  useEffect(() => {
    if (!ready) return;
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, userType, userOrg]);

  const handleDelete = (item) => {
    setDeleteItem(item);
    setShowDeleteSKUModal(true);
    setOpenMenuId(null);
  };

  const handleFormSubmit = () => {
    // After creating or editing a product, refetch using the same endpoint logic
    if (ready) fetchItems();
  };

  // Optional: show an ordered subset of common fields first
  const preferredOrder = useMemo(
    () => ["Ideal for", "Ingredients", "Product of", "Contact"],
    []
  );

  // Filtered items based on search
  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;

    return items.filter((it) => {
      const code = String(it.code || "").toLowerCase();
      const name = String(it.name || "").toLowerCase();

      // Flatten metadata values to a searchable string
      const metaVals = Object.values(it.metadata || {})
        .map((v) => String(v))
        .join(" ")
        .toLowerCase();

      return code.includes(q) || name.includes(q) || metaVals.includes(q);
    });
  }, [items, search]);

  return (
    <>
      <Helmet>
        <title>Product SKUs | BLEEP</title>
        <meta
          name="description"
          content="Manage your brand's items/products/SKUs here"
        />
      </Helmet>

      {styles}

      <div className="d-flex">
        <Sidenav domain={domain} />

        <div
          style={{
            flex: 1,
            position: "relative",
            height: "calc(100vh - 94px)",
            overflowY: "auto",
            overflowX: "hidden",
            backgroundColor: "#ebebeb",
          }}
        >
          {/* Header */}
          <header
            style={{
              position: "relative",
              paddingInline: "30px",
              paddingBlock: "30px",
              color: "#fff",
              background:
                "linear-gradient(135deg, rgba(255,81,0,1) 0%, rgba(255,0,0,1) 100%)",
              overflow: "hidden",
            }}
          >
            <div
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage: "url('/images/background-art.jpg')",
                backgroundRepeat: "repeat",
                backgroundSize: "400px 400px",
                backgroundPosition: "center",
                mixBlendMode: "overlay",
                opacity: 0.1,
                zIndex: 0,
                pointerEvents: "none",
              }}
            />
            <div
              className="row d-flex align-items-center"
              style={{ position: "relative", zIndex: 1 }}
            >
              <div
                className="col"
                style={{ padding: 10 }}
              >
                <b
                  style={{
                    fontSize: "28px",
                    textShadow: "#1c1c1c 1px 2px 0px",
                  }}
                >
                  <i className="fas fa-box" /> Product SKUs
                </b>
                <p
                  style={{
                    paddingTop: "8px",
                    textShadow: "#1c1c1c 1px 1px 0px",
                    marginBottom: 14,
                  }}
                >
                  Manage your brand's Product SKUs here
                </p>
                <button
                  type="button"
                  className="btn btn-light btn-lg"
                  style={{
                    borderRadius: "30px",
                    boxShadow: "rgba(0, 0, 0, 0.25) 0px 3px 8px",
                    paddingBlock: "10px",
                    paddingInline: "30px",
                  }}
                  onClick={handleShowCreateProductModal}
                >
                  <span style={{ marginRight: 10 }}>
                    <i className="fa-solid fa-plus-circle" />
                  </span>
                  <b>Create Product SKU</b>
                </button>
              </div>
            </div>
          </header>

          {/* Search */}
          <div className="search-wrap">
            <div
              className={`search-bar${searchFocused ? " focused" : ""}`}
              role="search"
            >
              <i className="fas fa-search search-icon" aria-hidden="true" />
              <input
                ref={searchInputRef}
                className="search-input"
                type="search"
                placeholder="Search SKUs by code, name, or metadata"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                aria-label="Search product SKUs"
              />
              {!search && (
                <kbd className="search-shortcut" title="Press / to focus">/</kbd>
              )}
              {search && (
                <button
                  className="search-clear"
                  onClick={() => { setSearch(""); searchInputRef.current?.focus(); }}
                  title="Clear search"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          <div style={{ padding: 20 }}>
            {!ready ? (
              <div className="empty-state">Loading user…</div>
            ) : isLoading ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "240px",
                  color: "#6b7280",
                  gap: 12,
                }}
              >
                <i
                  className="fas fa-spinner fa-spin"
                  style={{ fontSize: "28px" }}
                  aria-hidden="true"
                />
                <div style={{ fontSize: 13 }}>Loading products…</div>
              </div>
            ) : items.length === 0 ? (
              <div className="empty-state">
                No products yet. Click{" "}
                <b
                  onClick={handleShowCreateProductModal}
                  style={{ cursor: "pointer" }}
                >
                  <i className="fas fa-plus-circle"></i> Create Product SKU
                </b>{" "}
                to add your first SKU!
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="empty-state">
                No results for <b>{search}</b>. Try a different query.
              </div>
            ) : (
              <>
                <div className="sku-toolbar">
                  <div className="count-badge">
                    <i className="fas fa-box" />
                    {search
                      ? `${filteredItems.length} of ${items.length} SKUs`
                      : `${items.length} SKU${items.length !== 1 ? "s" : ""}`}
                  </div>
                </div>

                <div className="card-grid">
                  {filteredItems.map((item) => {
                    const meta = item.metadata || {};
                    const preferred = preferredOrder
                      .filter((k) => meta[k] != null && String(meta[k]).trim())
                      .map((k) => [k, meta[k]]);
                    const preferredSet = new Set(preferred.map(([k]) => k));
                    const rest = Object.entries(meta).filter(
                      ([k, v]) => !preferredSet.has(k) && v != null && String(v).trim()
                    );
                    const allMeta = [...preferred, ...rest];

                    const menuOpen = openMenuId === item._id;
                    const avatarColor = getSkuColor(item.name || item.code || "");
                    const avatarInitials = getSkuInitials(item.name || item.code || "");

                    return (
                      <article
                        key={item._id}
                        className="sku-card"
                        style={menuOpen ? { zIndex: 100 } : undefined}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Header */}
                        <div className="sku-header">
                          <div className="sku-left">
                            <div className="sku-avatar" style={{ background: avatarColor }}>
                              {avatarInitials}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div className="sku-name" title={item.name}>{item.name || "–"}</div>
                              <span className="sku-code">
                                <i className="fas fa-hashtag" style={{ fontSize: 9 }} />
                                {item.code || "–"}
                              </span>
                            </div>
                          </div>

                          <div className="card-actions">
                            <button
                              className="icon-ellipsis"
                              aria-haspopup="menu"
                              aria-expanded={menuOpen}
                              title="Actions"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuId(menuOpen ? null : item._id);
                              }}
                            >
                              <i className="fas fa-ellipsis-h" />
                            </button>

                            {menuOpen && (
                              <div className="menu-pop" role="menu" onClick={(e) => e.stopPropagation()}>
                                <button
                                  className="menu-item"
                                  role="menuitem"
                                  onClick={() => handleEditProduct(item)}
                                >
                                  <i className="fas fa-pen-to-square" /> Edit
                                </button>
                                <button
                                  className="menu-item danger"
                                  role="menuitem"
                                  onClick={() => handleDelete(item)}
                                >
                                  <i className="fas fa-trash-alt" /> Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {allMeta.length > 0 && (
                          <>
                            <div className="divider" />
                            <div className="meta-stack">
                              {allMeta.map(([k, v]) => (
                                <div key={k} className="meta-item">
                                  <span className="meta-icon-label">
                                    <i className={`fas ${metaIcon(k)}`} />
                                    {k}
                                  </span>
                                  <span className="meta-content">{String(v)}</span>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </article>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <CreateProduct
        domain={domain}
        show={showCreateProductModal}
        handleClose={handleCloseCreateProductModal}
        handleFormSubmit={handleFormSubmit}
        user={userId}
        client={userOrg}
      />

      <EditProduct
        domain={domain}
        show={showEditProductModal}
        productId={editProductId}
        handleClose={handleCloseEditProductModal}
        handleFormSubmit={() => {
          handleFormSubmit();
          handleCloseEditProductModal();
        }}
      />

      <DeleteProductSKUModal
        show={showDeleteSKUModal}
        handleClose={() => setShowDeleteSKUModal(false)}
        itemId={deleteItem?._id}
        itemName={deleteItem?.name}
        itemCode={deleteItem?.code}
        onDeleted={fetchItems}
      />
    </>
  );
};

export default ProductSKUs;
